import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { getStoredAmplitudeUserId, getAmplitudeContext } from './amplitudeIdentity'
import AuthService from './AuthService'

let engagementLoad: Promise<boolean> | undefined
let engagementStopped = false
let engagementNavigate: ((url: string) => void) | undefined

export function setEngagementRouter(navigate: (url: string) => void) {
  engagementNavigate = navigate
  window.engagement?.setRouter(navigate)
}

export function stopGuidesAndSurveys() {
  engagementStopped = Boolean(engagementLoad)
  window.engagement?.shutdown()
  return Boolean(engagementLoad)
}

export function initGuidesAndSurveysForCurrentUser(env = process.env): Promise<boolean> {
  if (
    env.REACT_APP_ENABLE_GUIDES_SURVEYS !== 'true'
    || !isAmplitudeEnabledForCurrentEnv(env)
    || !AuthService.getAccessToken()
    || !getStoredAmplitudeUserId()
    || localStorage.getItem('spoof_origin_user') !== null
    || engagementStopped
  ) {
    return Promise.resolve(false)
  }

  if (!engagementLoad) {
    engagementLoad = import('@amplitude/engagement-browser').then(async ({ plugin: engagementPlugin }) => {
      if (engagementStopped || !AuthService.getAccessToken() || localStorage.getItem('spoof_origin_user') !== null) {
        return false
      }
      const plugin = engagementPlugin()
      if (engagementNavigate) window.engagement.setRouter(engagementNavigate)
      await amplitude.add(plugin).promise
      if (engagementStopped || !AuthService.getAccessToken() || localStorage.getItem('spoof_origin_user') !== null) {
        window.engagement.shutdown()
        return false
      }
      return true
    }).catch((error) => {
      console.warn('Unable to initialize Amplitude Guides and Surveys:', error)
      return false
    })
  }

  return engagementLoad
}

export function isAmplitudeEnabledForCurrentEnv(env = process.env) {
  return Boolean(
    env.REACT_APP_AMPLITUDE_API_KEY
      && (
        env.REACT_APP_APP_ENV === 'production'
        || env.REACT_APP_ENABLE_AMPLITUDE_NON_PROD === 'true'
      ),
  )
}

export function initAmplitudeForCurrentEnv(env = process.env) {
  const apiKey = env.REACT_APP_AMPLITUDE_API_KEY

  if (!apiKey || !isAmplitudeEnabledForCurrentEnv(env)) {
    console.error("Missing Amplitude API key, not tracking will be performed")
    return false
  }

  if (env.REACT_APP_APP_ENV === 'production' || env.REACT_APP_ENABLE_AMPLITUDE_NON_PROD === 'true') {
    console.log("NOTE: Amplitude tracking is enabled for production");

    const storedUserId = getStoredAmplitudeUserId()
    const sessionReplayTracking = sessionReplayPlugin({
      sampleRate: 0.1,
      privacyConfig: {
        maskSelector: ['[data-amp-mask]'],
      },
    });
    amplitude.add(sessionReplayTracking);
    amplitude.add({
      name: 'orbit-context',
      type: 'enrichment',
      setup: async () => undefined,
      execute: async (event) => {
        event.event_properties = { ...event.event_properties, ...getAmplitudeContext() }
        return event
      },
    });
    amplitude.init(apiKey, {
      autocapture: {
        elementInteractions: true,
        frustrationInteractions: true,
      },
      userId: storedUserId || undefined,
      // Pre-login visitors (and bots that never log in) stay opted out, so
      // nothing - including session replay and autocaptured clicks - is sent
      // until amplitude.setOptOut(false) runs on successful login/refresh.
      optOut: !AuthService.getAccessToken(),
    });
  }
  else {
      console.log("NOTE: This is not considered production, Amplitude tracking is disabled");
  }

  return true
}

